import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { productService, Product, FilterOptions } from '../services/productService';
import ProductTableView from '../components/products/ProductTableView';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { UserRole } from '../types';
import { MagnifyingGlassIcon, FunnelIcon, PrinterIcon, EyeIcon, TrashIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import { sortSizes } from '../utils/sortUtils';
import { useZebraPrinter } from '../hooks/useZebraPrinter';
import { QRPreviewModal } from '../components/QRPreviewModal';
import { generateProductLabelZPL } from '../utils/zplUtils';
import { ProductEntryModal } from '../components/products/ProductEntryModal';
import { CentralEditModal } from '../components/products/CentralEditModal';
import { DeleteWarningModal } from '../components/common/DeleteWarningModal';
import CustomCombobox from '../components/common/Combobox';

interface ProductGroup {
  cod_producto: string;
  marca: string;
  precio: number;
  products: Product[];
  totalStock: number;
  colors: string[];
  sizes: string[];
  matrix: { [colorSize: string]: Product };
}

type ViewMode = 'matrix' | 'table';

export const ProductsPage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.rol_usr === UserRole.ADMIN;
  const location = useLocation();
  const isPreOrderView = location.pathname === '/pre-orders';

  const [viewMode, setViewMode] = useState<ViewMode>(isPreOrderView ? 'table' : 'matrix');
  const [inventoryType, setInventoryType] = useState<'maestro' | 'novias'>('maestro');

  const initialIsPreOrder = isPreOrderView || new URLSearchParams(location.search).get('preOrder') === 'true';
  const [filterPreOrder, setFilterPreOrder] = useState(initialIsPreOrder);
  const [preOrderTab, setPreOrderTab] = useState<'pending' | 'received' | 'completed'>('pending');
  // ... rest of state

  // Initialize filters from URL or Route
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlPreOrder = params.get('preOrder') === 'true';

    if (isPreOrderView || urlPreOrder) {
      setFilterPreOrder(true);
      setViewMode('table'); // Force Table View
      setSortBy('created_at');
      setSortOrder('desc'); // Newest to oldest
    } else {
      setFilterPreOrder(false);
      // Optional: don't reset sorting if navigating normally, 
      // but to be safe we can ensure it defaults to the standard
      setSortBy('cod_producto');
      setSortOrder('asc');
    }
  }, [location.pathname, location.search, isPreOrderView]);

  // ... (rest of filtering logic)

  // In render:
  // Title
  // <h1 className="text-3xl font-bold text-gray-900">{isPreOrderView ? 'Pre-orders' : 'Inventario'}</h1>

  // Hide Buckets
  // {!isPreOrderView && !filterPreOrder && ( ... )} -- Wait, if isPreOrderView is true, we hide buckets?
  // User said "Convertir Pre-orders en vista independiente".
  // "El módulo debe mostrarse como una sección propia... no como un filtro del inventario".
  // But we still need to know if we are seeing Pre-orders from Maestro or Novias?
  // Or do we show mixed?
  // "Debe tener... su propio encabezado".
  // "Mantener filtros...". 
  // If I hide buckets, how do I see "Novias Pre-orders"?
  // Maybe I should keep the buckets but distinct? Or maybe the user implies unified view?
  // "El módulo debe mostrarse como una sección propia...".
  // If I look at the query logic, when `filterPreOrder` is true, we query `type='all'` effectively?
  // `type: (filterInvoice || filterPreOrder) ? 'all' : inventoryType` -> Yes.
  // So the buckets are irrelevant for fetching, but maybe relevant for visual filtering "Maestro vs Novias" inside Pre-orders?
  // The current buckets switch `inventoryType` state. 
  // If I force `type='all'` in query when `filterPreOrder` is true, checking `inventoryType` state might be redundant for *fetching* but maybe useful for *post-filtering* if desired.
  // But the query sends `all`.
  // So buckets should be hidden or disabled?
  // User didn't explicitly say "Hide buckets", but "No modificar lógica backend... Solo separar visualmente".
  // I will hide the buckets to make it look like a standalone module, assuming 'all' is what they want (consolidated pre-orders).

  // Update Render:
  // ...

  const [filterOutOfStock, setFilterOutOfStock] = useState(false);
  const [filterInactive, setFilterInactive] = useState(false);
  const [filterRecent, setFilterRecent] = useState(false); // New Filter state
  const [sortBy, setSortBy] = useState(initialIsPreOrder ? 'created_at' : 'cod_producto');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(initialIsPreOrder ? 'desc' : 'asc');
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  /* Duplicate removed */
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [filterColor, setFilterColor] = useState('');
  const [filterTalla, setFilterTalla] = useState('');
  const [filterMarca, setFilterMarca] = useState('');
  const [filterInvoice, setFilterInvoice] = useState('');
  const [availableInvoices, setAvailableInvoices] = useState<string[]>([]);

  // Delete Invoice State
  const [deleteInvoiceModalOpen, setDeleteInvoiceModalOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState<string | null>(null);
  const [deleteInvoiceWarnings, setDeleteInvoiceWarnings] = useState<string[]>([]);
  const [isSoftDeleteInvoice, setIsSoftDeleteInvoice] = useState(false);
  const [isDeletingInvoice, setIsDeletingInvoice] = useState(false);

  const [showFilters, setShowFilters] = useState(false);
  const [showDelivered, setShowDelivered] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showCentralEditModal, setShowCentralEditModal] = useState(false);
  const [selectedProductToEdit, setSelectedProductToEdit] = useState<Product | null>(null);
  const [previewProduct, setPreviewProduct] = useState<Product | null>(null);
  const [globalOptions, setGlobalOptions] = useState<FilterOptions | null>(null);

  // Hook Zebra
  const { 
    printLabel, 
    loading: printing,
    isUSBConnected,
    connectUSB,
    usbDeviceName
  } = useZebraPrinter();



  // Form state


  // handleSmartSearch removed as global search is now integrated into loadProducts

  // Debounce para el término de búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset page on search
    }, 350);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Variable de control para evitar race conditions en fetch
  const fetchCount = React.useRef(0);

  useEffect(() => {
    loadProducts();
  }, [currentPage, debouncedSearchTerm, inventoryType, filterPreOrder, filterOutOfStock, filterInactive, filterRecent, filterColor, filterTalla, filterMarca, filterInvoice, showDelivered, itemsPerPage, sortBy, sortOrder]);

  useEffect(() => {
    loadFilterOptions();
  }, []);

  const loadFilterOptions = async () => {
    try {
      const options = await productService.getFilterOptions();
      setGlobalOptions(options);
      setAvailableInvoices(options.invoices || []);
    } catch (error) {
      console.error('Error loading filter options:', error);
    }
  };

  // Computed values for filters - NOW GLOBAL
  // const location = useLocation(); // Removed duplicate

  // Initialize filters from URL if present
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlPreOrder = params.get('preOrder') === 'true';
    if (isPreOrderView || urlPreOrder) {
      setFilterPreOrder(true);
      setViewMode('table'); // Force Table View
    } else {
      setFilterPreOrder(false);
    }
  }, [location.pathname, location.search, isPreOrderView]);

  const uniqueColors = useMemo(() => {
    if (!globalOptions) return [];
    
    // Si hay una marca seleccionada, usamos la lista vinculada marca-color
    if (filterMarca) {
      const colorsForBrand = inventoryType === 'novias'
        ? globalOptions.novias?.brandColors?.filter(bc => bc.brand === filterMarca).map(bc => bc.color) || []
        : globalOptions.maestro?.brandColors?.filter(bc => bc.brand === filterMarca).map(bc => bc.color) || [];
      return Array.from(new Set(colorsForBrand)).sort((a, b) => a.localeCompare(b));
    }

    const colors = inventoryType === 'novias'
      ? globalOptions.novias?.colores || []
      : globalOptions.maestro?.colores || [];

    // Alpha sort, remove duplicates
    return Array.from(new Set(colors)).sort((a, b) => a.localeCompare(b));
  }, [globalOptions, inventoryType, filterMarca]);

  const uniqueSizes = useMemo(() => {
    if (!globalOptions) return [];
    
    let sizes = [];
    if (filterMarca) {
      sizes = inventoryType === 'novias'
        ? globalOptions.novias?.brandSizes?.filter(bs => bs.brand === filterMarca).map(bs => bs.size) || []
        : globalOptions.maestro?.brandSizes?.filter(bs => bs.brand === filterMarca).map(bs => bs.size) || [];
    } else {
      sizes = inventoryType === 'novias'
        ? globalOptions.novias?.tallas || []
        : globalOptions.maestro?.tallas || [];
    }

    // Custom sort for sizes
    return Array.from(new Set(sizes)).sort((a, b) => {
      const normalize = (s: string) => s.toUpperCase().trim();
      const normA = normalize(a);
      const normB = normalize(b);

      // 1. Strict numeric check (integers or decimals, but NOT 2XL)
      const isStrictNumber = (s: string) => /^\d+(\.\d+)?$/.test(s);
      const isNumA = isStrictNumber(normA);
      const isNumB = isStrictNumber(normB);

      if (isNumA && isNumB) {
        return parseFloat(normA) - parseFloat(normB);
      }

      // 2. Standard Size Mapping
      const sizeOrder: { [key: string]: number } = {
        'XXXS': 1, '3XS': 1,
        'XXS': 2, '2XS': 2,
        'XS': 3,
        'S': 4,
        'M': 5,
        'L': 6,
        'XL': 7,
        'XXL': 8, '2XL': 8,
        'XXXL': 9, '3XL': 9,
        'XXXXL': 10, '4XL': 10,
        'XXXXXL': 11, '5XL': 11,
        'UNITALLA': 100, 'ONE SIZE': 100, 'U': 100
      };

      const orderA = sizeOrder[normA];
      const orderB = sizeOrder[normB];

      if (orderA && orderB) {
        return orderA - orderB;
      }

      // If one is mapped size and other is strict number
      // Numbers first (e.g. 14 vs L -> 14 is a number filter, L is a letter filter. Usually separates them)
      if (isNumA && orderB) return -1;
      if (orderA && isNumB) return 1;

      // 3. Fallback to alpha
      return normA.localeCompare(normB);
    });
  }, [globalOptions, inventoryType]);

  const uniqueBrands = useMemo(() => {
    if (!globalOptions) return [];
    const brands = inventoryType === 'novias'
      ? globalOptions.novias?.marcas || []
      : globalOptions.maestro?.marcas || [];

    // Alpha sort, remove duplicates
    return Array.from(new Set(brands)).sort((a, b) => a.localeCompare(b));
  }, [globalOptions, inventoryType]);

  const clearFilters = () => {
    setSearchTerm('');
    setFilterColor('');
    setFilterTalla('');
    setFilterMarca('');
    setFilterPreOrder(false);
    setFilterOutOfStock(false);
    setFilterInactive(false);
    setFilterInactive(false);
    setFilterRecent(false);
    setFilterInvoice('');
    setSortBy('cod_producto');
    setSortOrder('asc');
  };

  // Centralized filtering for Pre-orders and other criteria that affects BOTH views
  const effectiveProducts = useMemo(() => {
    return products.filter(product => {
      // 1. Pre-order Visibility Rule:
      // - If product is Pre-order AND filterPreOrder is FALSE AND filterOutOfStock is FALSE -> HIDE IT
      // (Unless it's out of stock view, where we might want to see everything? User said "me refiero a todos los cambios como que no se muestren los Pre-orders en la vista normal". 
      // The original logic was: if (product.pre_order && !filterPreOrder && !filterOutOfStock) return; 
      // This implies if filterOutOfStock is TRUE, we SHOW pre-orders? 
      // Let's stick to the original logic for now, but apply it globally.)

      if (product.pre_order && !filterPreOrder && !filterOutOfStock) {
        return false;
      }
      return true;
    });
  }, [products, filterPreOrder, filterOutOfStock]);

  // Agrupar productos por código con matriz de color x talla
  const groupedProducts = useMemo(() => {
    const groups: { [key: string]: ProductGroup } = {};

    effectiveProducts.forEach(product => {
      // Normalizar el código: quitar espacios y convertir a mayúsculas
      const code = (product.cod_producto || 'SIN_CODIGO').trim().toUpperCase();

      // Ignorar productos placeholder de proveedores
      if (code.startsWith('PROVEEDOR-')) return;

      if (!groups[code]) {
        groups[code] = {
          cod_producto: code,
          marca: product.marca || '',
          precio: product.precio || 0,
          products: [],
          totalStock: 0,
          colors: [],
          sizes: [],
          matrix: {}
        };
      }

      groups[code].products.push(product);
      groups[code].totalStock += product.stock || 0;

      // Normalizar color y talla
      const normalizedColor = product.color?.trim().toUpperCase();
      const normalizedSize = product.talla?.trim();

      // Agregar color único
      if (normalizedColor && !groups[code].colors.includes(normalizedColor)) {
        groups[code].colors.push(normalizedColor);
      }

      // Agregar talla única
      if (normalizedSize && !groups[code].sizes.includes(normalizedSize)) {
        groups[code].sizes.push(normalizedSize);
      }

      // Crear key para la matriz con valores normalizados
      const key = `${normalizedColor || 'N/A'}_${normalizedSize || 'N/A'}`;
      // Si ya existe un producto en esta posición, sumar el stock
      if (groups[code].matrix[key]) {
        groups[code].matrix[key].stock = (groups[code].matrix[key].stock || 0) + (product.stock || 0);
      } else {
        groups[code].matrix[key] = { ...product, color: normalizedColor || null, talla: normalizedSize || null };
      }
    });

    // Ordenar colores y tallas
    Object.values(groups).forEach(group => {
      group.colors.sort();
      group.sizes = sortSizes(group.sizes);
    });

    return Object.values(groups).sort((a, b) =>
      a.cod_producto.localeCompare(b.cod_producto)
    );
  }, [effectiveProducts]);

  // Cache ref to store filter results
  const productsCache = React.useRef<{ [key: string]: { data: Product[], totalPages: number } }>({});

  const getCacheKey = () => {
    return JSON.stringify({
      page: currentPage,
      limit: itemsPerPage,
      search: debouncedSearchTerm.trim(),
      type: (filterInvoice || filterPreOrder) ? 'all' : inventoryType,
      color: filterColor || undefined,
      talla: filterTalla || undefined,
      marca: filterMarca || undefined,
      preOrder: filterPreOrder,
      outOfStock: filterOutOfStock,
      inactive: filterInactive,
      recent: filterRecent,
      showDelivered,
      invoiceId: filterInvoice || undefined,
      sortBy,
      order: sortOrder || undefined
    });
  };

  const loadProducts = async () => {
    const cacheKey = getCacheKey();
    const currentFetchId = ++fetchCount.current;

    // Check cache first
    if (productsCache.current[cacheKey]) {
      const cached = productsCache.current[cacheKey];
      setProducts(cached.data);
      setTotalPages(cached.totalPages);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      // Cargar solo 200 productos por defecto para mejorar rendimiento
      const limit = itemsPerPage;
      const response = await productService.getProducts({
        page: currentPage,
        limit: limit,
        search: debouncedSearchTerm.trim(),
        // If filtering by Invoice OR Pre-order OR Searching, fetch ALL types to combine them
        type: (filterInvoice || filterPreOrder || debouncedSearchTerm.trim()) ? 'all' : inventoryType,
        color: filterColor || undefined,
        talla: filterTalla || undefined,
        marca: filterMarca || undefined,
        preOrder: filterPreOrder,
        outOfStock: filterOutOfStock,
        inactive: filterInactive,
        recent: filterRecent, // Pass recent filter
        showDelivered,
        invoiceId: filterInvoice || undefined,
        sortBy,
        order: sortOrder || undefined
      });

      // Avoid race conditions
      if (currentFetchId !== fetchCount.current) return;

      // Update cache
      productsCache.current[cacheKey] = {
        data: response.data,
        totalPages: response.pagination.totalPages
      };

      setProducts(response.data);
      setTotalPages(response.pagination.totalPages);
    } catch (error: any) {
      if (currentFetchId !== fetchCount.current) return;
      toast.error('Error al cargar productos');
      console.error(error);
    } finally {
      if (currentFetchId === fetchCount.current) {
        setLoading(false);
      }
    }
  };

  const handleCorrect = (product: Product) => {
    if (!isAdmin) return;
    setSelectedProductToEdit(product);
    setShowCentralEditModal(true);
  };

  const invalidateCache = () => {
    productsCache.current = {};
  };



  const handleEdit = (product: Product) => {
    // Protección extra por si acaso
    if (!isAdmin) return;

    setEditingProduct(product);
    setEditingProduct(product);
    setShowModal(true);
  };

  const handleDelete = async (product: Product) => {
    if (!isAdmin) return;

    if (window.confirm('¿Estás seguro de desactivar este producto? Dejará de aparecer activo en el inventario pero se mantendrá en la base de datos.')) {
      try {
        await productService.deleteProduct(product.id_producto, product.inventoryType);
        toast.success('Producto desactivado exitosamente');
        invalidateCache();
        loadProducts();
      } catch (error) {
        toast.error('Error al desactivar producto');
      }
    }
  };

  const handleActivate = async (id: number) => {
    if (!isAdmin) return;

    if (window.confirm('¿Estás seguro de activar este producto?')) {
      try {
        await productService.activateProduct(id);
        toast.success('Producto activado exitosamente');
        invalidateCache();
        loadProducts();
      } catch (error) {
        toast.error('Error al activar producto');
      }
    }
  };

  // handleDeliver removed as delivery is now automatic based on invoice activation

  const handleDeleteInvoiceClick = async (invoiceId: string) => {
    try {
      const res = await productService.checkInvoiceDependencies(invoiceId);
      if (res.success && res.data) {
        const warnings: string[] = [];
        let soft = false;

        warnings.push(`Este invoice contiene ${res.data.totalProducts} producto(s).`);

        if (res.data.hasSales) {
          warnings.push(`ATENCIÓN: Se encontraron ${res.data.salesCount} operación(es) vinculadas a los productos de esta caja (Ej. Ventas o Kardex).`);
          warnings.push('Eliminar el invoice afectará la consistencia histórica de esos productos. Solo se realizará una desactivación masiva (Soft Delete) de los mismos.');
          soft = true;
        } else {
          warnings.push('Ninguno de los productos registrados en este invoice tiene ventas.');
          warnings.push('Se eliminarán todos permanentemente de la base de datos (Hard Delete).');
        }

        if (res.data.hasPreOrders) {
          warnings.push(`Hay ${res.data.preOrdersCount} producto(s) asignado(s) a Pre-Orders dentro de este invoice.`);
        }

        if (res.data.hasReceivedItems) {
          warnings.push(`Hay ${res.data.receivedCount} producto(s) que ya fueron marcados como RECIBIDOS físicamente.`);
        }

        warnings.push('🚨 IMPORTANTE: Si ya imprimiste etiquetas físicas para estos productos, por favor destrúyelas para evitar errores de escaneo.');

        setDeleteInvoiceWarnings(warnings);
        setIsSoftDeleteInvoice(soft);
        setInvoiceToDelete(invoiceId);
        setDeleteInvoiceModalOpen(true);
      }
    } catch (error) {
      toast.error('Error al verificar dependencias del invoice');
    }
  };

  const handleConfirmDeleteInvoice = async () => {
    if (!invoiceToDelete) return;
    setIsDeletingInvoice(true);
    try {
      const res = await productService.deleteInvoice(invoiceToDelete);
      toast.success(res.message || 'Invoice eliminado exitosamente');
      setDeleteInvoiceModalOpen(false);
      setFilterInvoice('');
      invalidateCache();
      loadProducts();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al eliminar el invoice');
    } finally {
      setIsDeletingInvoice(false);
    }
  };


  const handlePrintQR = async (product: Product) => {
    // 1. Verificar si tiene qr_code_value
    let qrValue = product.qr_code_value;

    // Si no tiene, generamos uno y lo guardamos (opcional, pero recomendado)
    if (!qrValue) {
      // Generar valor único: PROD-{ID}-{TIMESTAMP} o usar el código y generar si no existe
      // Usemos el código de producto si existe, si no, generamos.
      // Pero para unicidad global, mejor un prefijo.
      // El backend ya tiene logica create, pero aquí estamos en existing.
      const newQr = `PROD-${product.id_producto}-${Date.now()}`;
      try {
        await productService.updateProduct(product.id_producto, {
          qr_code_value: newQr,
          type: product.inventoryType // Enviar tipo para Novias
        });
        qrValue = newQr;
        toast.success('Código QR generado y guardado');
        invalidateCache();
        // Actualizar lista localmente para reflejar cambio
        setProducts(prev => prev.map(p => p.id_producto === product.id_producto ? { ...p, qr_code_value: newQr } : p));
      } catch (e) {
        console.error(e);
      }
    }

    // 2. Fetch Variants and Generate ZPL
    const { sizes, colors } = await productService.getVariants(product.cod_producto || '');

    const zpl = generateProductLabelZPL(product, {
      availableSizes: sizes,
      availableColors: colors
    });

    // 3. Imprimir
    const success = await printLabel(zpl);
    if (success) {
      toast.success('Enviado a imprimir');
    } else {
      toast.error('No se encontró impresora Zebra. Revisa la conexión.');
    }
  };

  const handlePreviewQR = async (product: Product) => {
    // 1. Verificar si tiene qr_code_value (mismo logic que print)
    let qrValue = product.qr_code_value;

    if (!qrValue) {
      const newQr = `PROD-${product.id_producto}-${Date.now()}`;
      try {
        await productService.updateProduct(product.id_producto, {
          qr_code_value: newQr,
          type: product.inventoryType // Enviar tipo para Novias
        });
        qrValue = newQr;
        invalidateCache();
        // update local state
        const updatedProduct = { ...product, qr_code_value: newQr };
        setProducts(prev => prev.map(p => p.id_producto === product.id_producto ? updatedProduct : p));
        setPreviewProduct(updatedProduct);
      } catch (e) {
        console.error(e);
        toast.error('Error al generar QR');
        return;
      }
    } else {
      setPreviewProduct(product);
    }
    setShowPreviewModal(true);
  };

  const formatCurrency = (value: number | null) => {
    if (!value) return '$0.00';
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(value);
  };

  const handleCopyInventory = (group: any) => {
    let text = `${group.cod_producto}\n`;
    if (group.marca) text += `${group.marca} • ${formatCurrency(group.precio)}\n`;
    else text += `${formatCurrency(group.precio)}\n`;
    text += `Stock Total: ${group.totalStock}\n\n`;

    group.colors.forEach((color: string) => {
      const availableSizes: string[] = [];
      group.sizes.forEach((size: string) => {
        const product = group.matrix[`${color}_${size}`];
        if (product && product.stock > 0) {
          availableSizes.push(`${size} (${product.stock})`);
        }
      });
      if (availableSizes.length > 0) {
        text += `${color}: ${availableSizes.join(', ')}\n`;
      }
    });

    navigator.clipboard.writeText(text).then(() => {
      toast.success('Inventario copiado');
    }).catch(() => {
      toast.error('Error al copiar');
    });
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">{isPreOrderView ? 'Pre-orders' : 'Inventario'}</h1>
          {isAdmin && (
            <div className="flex space-x-2">
              <button
                onClick={connectUSB}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors ${
                  isUSBConnected 
                    ? 'bg-green-50 border-green-500 text-green-700' 
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
                title={isUSBConnected ? `Conectado a ${usbDeviceName}` : 'Configurar Impresora Zebra via USB'}
              >
                <PrinterIcon className="w-5 h-5" />
                <span>{isUSBConnected ? 'Impresora Lista' : 'Configurar USB'}</span>
              </button>
              <button
                onClick={() => {
                  setEditingProduct(null);
                  setShowModal(true);
                }}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                + Nuevo Producto
              </button>
            </div>
          )}
        </div>

        {/* Control de Tipo de Inventario (Maestro / Novias) - Ocultar en vista Pre-order */}
        {!filterPreOrder && !isPreOrderView && (
          <div className="bg-white rounded-lg p-2 shadow inline-flex mb-4">
            <button
              onClick={() => setInventoryType('maestro')}
              className={`px-4 py-2 rounded-md font-medium text-sm transition-colors ${inventoryType === 'maestro'
                ? 'bg-blue-100 text-blue-700'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              Inventario Maestro
            </button>
            <button
              onClick={() => setInventoryType('novias')}
              className={`px-4 py-2 rounded-md font-medium text-sm transition-colors ${inventoryType === 'novias'
                ? 'bg-purple-100 text-purple-700'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              Inventario Novias
            </button>
          </div>
        )}

        {/* Tabs para cambiar entre vistas - Hide in Pre-order view if matrix not supported, but user might want matrix of pre-orders? 
            Let's keep it but default to table. 
            Actually, user said "Pre-orders" view. Usually list. 
            Let's keep it visible. */}

        {/* Tabs específicos para Pre-orders */}
        {isPreOrderView && (
          <div className="bg-white rounded-lg shadow p-1 flex gap-1 mb-2 max-w-md">
            {[
              { id: 'pending', label: 'Pendientes', color: 'text-amber-600', bg: 'bg-amber-50' },
              { id: 'received', label: 'Recibidos', color: 'text-emerald-600', bg: 'bg-emerald-50' },
              { id: 'completed', label: 'Completados', color: 'text-blue-600', bg: 'bg-blue-50' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setPreOrderTab(tab.id as any);
                  setShowDelivered(tab.id !== 'pending');
                  setCurrentPage(1);
                }}
                className={`flex-1 py-2 px-3 rounded-md text-xs font-bold transition-all ${
                  preOrderTab === tab.id
                    ? `${tab.bg} ${tab.color} shadow-sm ring-1 ring-inset ring-current`
                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setViewMode('matrix')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${viewMode === 'matrix'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                  <span>Vista Matriz</span>
                </div>
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${viewMode === 'table'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <span>Vista Tabla</span>
                </div>
              </button>
            </nav>
          </div>
        </div>

        {/* Search and Filters - Visible in both modes */}
        <div className="bg-white rounded-lg shadow p-4 space-y-4">
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                placeholder="Buscar (Escanea o escribe código, marca, color...)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value.replace(/'/g, '-').replace(/\?/g, '_').toUpperCase())}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setDebouncedSearchTerm(searchTerm);
                    setCurrentPage(1);
                  }
                }}
                className="w-full pl-10 pr-4 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-2 rounded-lg border transition-colors flex items-center space-x-2 ${showFilters
                ? 'bg-blue-50 border-blue-500 text-blue-700'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
            >
              <FunnelIcon className="w-5 h-5" />
              <span>Filtros</span>
            </button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-4 border-t border-gray-200">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                <CustomCombobox
                  value={filterColor}
                  onChange={setFilterColor}
                  options={(uniqueColors.filter(Boolean) as string[])}
                  placeholder="Todos los colores"
                  allowCustom={true}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Talla</label>
                <CustomCombobox
                  value={filterTalla}
                  onChange={setFilterTalla}
                  options={(uniqueSizes.filter(Boolean) as string[])}
                  placeholder="Todas las tallas"
                  allowCustom={true}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Marca</label>
                <CustomCombobox
                  value={filterMarca}
                  onChange={setFilterMarca}
                  options={(uniqueBrands.filter(Boolean) as string[])}
                  placeholder="Todas las marcas"
                  allowCustom={true}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Invoice</label>
                <div className="flex gap-2 items-center">
                  <div className="flex-1">
                    <CustomCombobox
                      value={filterInvoice}
                      onChange={setFilterInvoice}
                      options={(availableInvoices.filter(Boolean) as string[])}
                      placeholder="Todas las invoices"
                      allowCustom={true}
                    />
                  </div>
                  {isAdmin && filterInvoice && (
                    <button
                      onClick={() => handleDeleteInvoiceClick(filterInvoice)}
                      className="p-2.5 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors shadow-sm border border-red-100"
                      title="Eliminar Invoice"
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>
              <div className="md:col-span-4 flex flex-wrap gap-4 items-center">
                {!isPreOrderView && (
                  <label className="flex items-center space-x-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filterPreOrder}
                      onChange={(e) => setFilterPreOrder(e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span>Mostrar solo PRE-ORDER</span>
                  </label>
                )}
                {/* Checkbox "Mostrar Entregados" eliminado a favor de pestañas */}

                <label className="flex items-center space-x-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filterOutOfStock}
                    onChange={(e) => setFilterOutOfStock(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span>Sin Stock (0 o menos)</span>
                </label>
                <label className="flex items-center space-x-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filterRecent}
                    onChange={(e) => setFilterRecent(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span>Recientes (7 días)</span>
                </label>
                <label className="flex items-center space-x-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filterInactive}
                    onChange={(e) => setFilterInactive(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span>Inactivos</span>
                </label>
                <button onClick={clearFilters} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">
                  Limpiar filtros
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Contenido según vista seleccionada */}
        {viewMode === 'table' ? (
          <ProductTableView
            products={effectiveProducts}
            loading={loading}
            onRefresh={() => { invalidateCache(); loadProducts(); }}
            inventoryType={filterInvoice ? 'maestro' : inventoryType}
            selectAllOnLoad={!!filterInvoice}
            sortField={sortBy}
            sortDirection={sortOrder as any}
            availableBrands={uniqueBrands}
            availableColors={uniqueColors}
            availableSizes={uniqueSizes}
            onSort={(field) => {
              if (sortBy === field) {
                setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
              } else {
                setSortBy(field);
                setSortOrder('asc');
              }
            }}
            onCorrect={handleCorrect}
          />
        ) : (
          <>
            {/* Products Grid */}
            <div className="space-y-6 relative">
              {loading && products.length === 0 ? (
                <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
                  Cargando...
                </div>
              ) : products.length === 0 ? (
                <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
                  No hay productos registrados
                </div>
              ) : (
                <>
                  {loading && (
                    <div className="absolute inset-0 bg-white/50 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-lg">
                      <div className="bg-white px-4 py-2 rounded-full shadow-md text-sm font-medium text-blue-600 flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Actualizando...
                      </div>
                    </div>
                  )}
                  {groupedProducts.map((group) => (
                  <div key={group.cod_producto} className="bg-white rounded-lg shadow overflow-hidden">
                    {/* Header del producto */}
                    <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                      <div className="flex justify-between items-center">
                        <div>
                          <h3 className="text-xl font-bold text-gray-900">{group.cod_producto}</h3>
                          <p className="text-sm text-gray-600 mt-1">
                            {group.marca} • {formatCurrency(group.precio)}
                          </p>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="text-right">
                            <p className="text-sm text-gray-500">Stock Total</p>
                            <p className="text-2xl font-bold text-gray-900">{group.totalStock}</p>
                          </div>
                          <button
                            onClick={() => handleCopyInventory(group)}
                            className="p-2 text-gray-500 hover:text-blue-600 bg-white hover:bg-blue-50 border border-gray-300 rounded-lg transition-colors shadow-sm"
                            title="Copiar Inventario"
                          >
                            <DocumentDuplicateIcon className="w-5 h-5" />
                          </button>
                          {isAdmin && (
                            <button
                              onClick={() => {
                                setEditingProduct(null);
                                setShowModal(true);
                              }}
                              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium shadow-sm"
                            >
                              + Agregar Variante
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Leyendas de Pendientes por Recibir / Invoices */}
                    {(() => {
                      const pendingProducts = group.products.filter(p => p.in_transit && p.invoice_id);
                      if (pendingProducts.length > 0) {
                        const pendingByInvoice = pendingProducts.reduce((acc, p) => {
                          if (!acc[p.invoice_id!]) acc[p.invoice_id!] = 0;
                          acc[p.invoice_id!] += (p.stock || 0);
                          return acc;
                        }, {} as Record<string, number>);

                        return (
                          <div className="bg-amber-50/50 px-6 py-3 border-b border-amber-100 flex flex-wrap gap-2 items-center">
                            <span className="text-xs font-bold text-amber-800 flex items-center gap-1.5">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                              Pendiente por recibir (Pre-embarque):
                            </span>
                            {Object.entries(pendingByInvoice).map(([invoice, qty]) => (
                              <span key={invoice} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-white text-amber-900 border border-amber-200 shadow-sm">
                                Invoice: {invoice} <span className="bg-amber-100 px-1.5 py-0.5 rounded text-amber-800">{qty} pzas</span>
                              </span>
                            ))}
                          </div>
                        );
                      }
                      return null;
                    })()}

                    {/* Matriz de Color x Talla */}
                    <div className="overflow-x-auto w-full">
                      <table className="w-full table-auto border-collapse">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-2 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap">
                              Color / Talla
                            </th>
                            {group.sizes.map((size) => (
                              <th key={size} className="px-1 py-2 text-center text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300">
                                {size}
                              </th>
                            ))}
                            <th className="px-2 py-2 text-center text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                              QTY
                            </th>
                            <th className="px-2 py-2 text-center text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                              Precio
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {group.colors.map((color) => {
                            const colorStock = group.sizes.reduce((sum, size) => {
                              const key = `${color}_${size}`;
                              const product = group.matrix[key];
                              return sum + (product?.stock || 0);
                            }, 0);

                            // Obtener el precio real de esta fila de color
                            const colorPrice = (() => {
                              for (const size of group.sizes) {
                                const p = group.matrix[`${color}_${size}`];
                                if (p?.precio != null) return p.precio;
                              }
                              return group.precio;
                            })();

                            return (
                              <tr key={color} className="hover:bg-gray-50">
                                <td className="px-2 py-2 text-sm font-medium text-gray-900 border-r border-gray-300 uppercase whitespace-nowrap">
                                  {color}
                                </td>
                                {group.sizes.map((size) => {
                                  const key = `${color}_${size}`;
                                  const product = group.matrix[key];
                                  const stock = product?.stock || 0;
                                  const hasStock = stock > 0;
                                  const isInTransit = product?.in_transit === true;
                                  const isInactive = product?.activo === false;

                                  return (
                                    <td
                                      key={size}
                                      className={`px-1 py-2 text-center border-r border-gray-300 relative group ${isAdmin && product ? 'cursor-pointer' : ''}`}
                                      onClick={() => isAdmin && product && handleEdit(product)}
                                    >
                                      <div className={`inline-flex flex-col items-center justify-center w-full min-w-[32px] px-1 py-1 rounded-md text-xs font-medium transition-all ${!product
                                        ? 'bg-gray-100 text-gray-400'
                                        : isInTransit
                                          ? 'bg-amber-50 text-amber-800 border border-amber-200'
                                          : isInactive
                                            ? 'bg-gray-200 text-gray-500 line-through'
                                            : hasStock
                                              ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                              : 'bg-red-100 text-red-800 hover:bg-red-200'
                                        }`}>
                                        <span>{product ? stock : '-'}</span>
                                        {isInTransit && <span className="text-[9px] uppercase font-bold text-amber-600 leading-none mt-0.5">TR</span>}
                                        {isInactive && !isInTransit && <span className="text-[9px] leading-none mt-0.5">(Off)</span>}
                                      </div>
                                      {isAdmin && product && (
                                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-black bg-opacity-50 transition-opacity rounded">
                                          <div className="flex space-x-2">
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleEdit(product);
                                              }}
                                              className="p-1.5 bg-white rounded-full hover:bg-gray-100"
                                              title="Editar"
                                            >
                                              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                              </svg>
                                            </button>
                                            {isInactive ? (
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleActivate(product.id_producto);
                                                }}
                                                className="p-1.5 bg-white rounded-full hover:bg-gray-100"
                                                title="Activar"
                                              >
                                                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                              </button>
                                            ) : (
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleDelete(product);
                                                }}
                                                className="p-1.5 bg-white rounded-full hover:bg-gray-100"
                                                title="Desactivar"
                                              >
                                                <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                                </svg>
                                              </button>
                                            )}
                                          </div>
                                          <div className="flex gap-1 bg-white rounded-full shadow-sm border border-gray-100 p-1">
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handlePreviewQR(product);
                                              }}
                                              className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                                              title="Ver QR"
                                            >
                                              <EyeIcon className="w-4 h-4 text-gray-600" />
                                            </button>
                                            <div className="w-px bg-gray-200"></div>
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handlePrintQR(product);
                                              }}
                                              className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                                              title="Imprimir QR"
                                              disabled={printing}
                                            >
                                              <PrinterIcon className="w-4 h-4 text-gray-600" />
                                            </button>
                                          </div>
                                        </div>
                                      )}
                                    </td>
                                  );
                                })}
                                <td className="px-2 py-2 text-center text-sm font-bold text-gray-900 whitespace-nowrap">
                                  {colorStock}
                                </td>
                                <td className="px-2 py-2 text-center text-sm text-gray-900 whitespace-nowrap">
                                  {formatCurrency(colorPrice)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Leyenda */}
                    <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
                      <div className="flex items-center justify-end space-x-6 text-xs">
                        <div className="flex items-center space-x-2">
                          <div className="w-4 h-4 bg-green-100 border border-green-300 rounded"></div>
                          <span className="text-gray-600">En Stock</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-4 h-4 bg-yellow-100 border border-yellow-300 rounded"></div>
                          <span className="text-gray-600">Stock Bajo</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-4 h-4 bg-red-100 border border-red-300 rounded"></div>
                          <span className="text-gray-600">Sin Stock</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-4 h-4 bg-gray-100 border border-gray-300 rounded"></div>
                          <span className="text-gray-600">No Disponible</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-4 h-4 bg-amber-100 border border-amber-300 rounded"></div>
                          <span className="text-gray-600">En Tránsito (TR)</span>
                        </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </>
        )}

        {/* Paginación - Visible en ambas vistas */}
        <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0 mt-6 pb-6 bg-white p-4 rounded-lg shadow border border-gray-100">

          {/* Selector de Items por página */}
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <span>Mostrar:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1); // Reset a primera página al cambiar límite
              }}
              className="border-gray-300 rounded text-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="200">200</option>
            </select>
            <span>por página</span>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
              >
                « Primera
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
              >
                ← Anterior
              </button>

              <span className="text-gray-700 text-sm px-2">
                Página <span className="font-bold">{currentPage}</span> de <span className="font-bold">{totalPages}</span>
              </span>

              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
              >
                Siguiente →
              </button>
            </div>
          )}
        </div>


        {/* Modal */}
        <ProductEntryModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setEditingProduct(null);
          }}
          onSuccess={() => {
            invalidateCache();
            loadProducts();
          }}
          productToEdit={editingProduct}
          initialInventoryType={inventoryType}
        />

        {/* Modal QR Preview */}
        {
          showPreviewModal && previewProduct && (
            <QRPreviewModal
              product={previewProduct}
              onClose={() => setShowPreviewModal(false)}
            />
          )
        }

        {selectedProductToEdit && (
          <CentralEditModal
            isOpen={showCentralEditModal}
            onClose={() => {
              setShowCentralEditModal(false);
              setSelectedProductToEdit(null);
            }}
            onSuccess={() => {
              invalidateCache();
              loadProducts();
            }}
            saleId={(selectedProductToEdit as any).preOrderDetails?.saleId || ''}
            saleItem={
              (selectedProductToEdit as any).preOrderDetails ? {
                ...(selectedProductToEdit as any).preOrderDetails,
                id: (selectedProductToEdit as any).preOrderDetails.id,
                productId: selectedProductToEdit.id_producto,
                productCode: selectedProductToEdit.cod_producto,
                productName: selectedProductToEdit.cod_producto,
                quantity: 1,
                unitPrice: selectedProductToEdit.precio,
                total: selectedProductToEdit.precio,
                isPreOrder: selectedProductToEdit.pre_order,
                inventoryType: selectedProductToEdit.inventoryType,
                product: selectedProductToEdit,
                sales: (selectedProductToEdit as any).preOrderDetails
              } : null
            }
            product={selectedProductToEdit}
          />
        )}
      </div>

      {invoiceToDelete && (
        <DeleteWarningModal
          isOpen={deleteInvoiceModalOpen}
          onClose={() => setDeleteInvoiceModalOpen(false)}
          onConfirm={handleConfirmDeleteInvoice}
          title={`Eliminar Invoice: ${invoiceToDelete}`}
          isSoftDelete={isSoftDeleteInvoice}
          isLoading={isDeletingInvoice}
          warnings={deleteInvoiceWarnings}
        />
      )}
    </Layout>
  );
};

export default ProductsPage;
